import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EarningRide } from '../../types/interfaces'
import { EarningsStatsCard } from '../components/EarningsStatsCard'
import { RidesTable } from '../components/RidesTable'
import { SortableTaleHeader } from '../components/SortableTableHeader'
import { StatusBadge } from '../components/StatusBadge'
import { formatDate, formatDateTime } from '../utils/date'
import { formatCurrency } from '../utils/number'
import { useNavigationAndSortEarningsTable } from './hooks/useNavigationAndSortEarningsTable'
import { useRides } from './hooks/useRides'

const showDownloadWeeklyReport = false

const Rides = () => {
  const { t } = useTranslation()

  const { handleChangePagination, handleSort, pagination, sortParams } =
    useNavigationAndSortEarningsTable()

  const {
    earningRides,
    earningRidesPage,
    earningRidesPerPage,
    earningStats,
    earningWeeklyReport,
    isLoadingEarningReport,
    isLoadingEarningRides,
    isLoadingEarningStats,
    totalEarningRides,
  } = useRides({
    limit: pagination.pageSize,
    page: pagination.page,
    sort: sortParams,
  })

  const headers: ColumnDef<EarningRide>[] = useMemo(
    () => [
      {
        accessorKey: 'creation_date',
        cell: ({ getValue }) => (
          <Typography variant="body2">
            {formatDate(getValue<Date>())}
          </Typography>
        ),
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_rides_headers.created_at')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'dropoff_completed_at',
        cell: ({ getValue }) => (
          <Typography variant="body2">
            {formatDateTime(getValue<Date>())}
          </Typography>
        ),
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_rides_headers.dropoff_at')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue<string>()
          return <StatusBadge label={status.toUpperCase()} status={status} />
        },
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_rides_headers.status')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'total_price',
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_rides_headers.total')}
            onSort={handleSort}
          />
        ),
      },
      {
        accessorKey: 'driver_earnings',
        header: ({ column }) => (
          <SortableTaleHeader
            column={column}
            label={t('earnings.latest_rides_headers.driver_earnings')}
            onSort={handleSort}
          />
        ),
      },
    ],
    [handleSort, t]
  )

  const weeklyReportLink = useMemo(() => {
    if (!earningWeeklyReport?.is_found) {
      return ''
    }

    return `${import.meta.env.VITE_DRIVER_EARNINGS_WEEKLY_REPORT_CLOUDFRONT_URL}${earningWeeklyReport?.url}`
  }, [earningWeeklyReport?.is_found, earningWeeklyReport?.url])

  const renderDownloadWeeklyReportButton = useCallback(() => {
    if (isLoadingEarningReport) {
      return (
        <Skeleton
          animation="wave"
          height={36}
          variant="rectangular"
          width={250}
        />
      )
    }

    if (!earningWeeklyReport?.is_found) {
      return (
        <Tooltip arrow title={t('earnings.weekly_report_not_found')}>
          <span>
            <Button
              disabled={!earningWeeklyReport?.is_found}
              endIcon={<DownloadIcon />}
              variant="contained"
            >
              <Typography variant="button">
                {t('earnings.download_weekly_report')}
              </Typography>
            </Button>
          </span>
        </Tooltip>
      )
    }

    return (
      <Button
        endIcon={<DownloadIcon />}
        href={weeklyReportLink}
        variant="contained"
      >
        <Typography variant="button">
          {t('earnings.download_weekly_report')}
        </Typography>
      </Button>
    )
  }, [
    earningWeeklyReport?.is_found,
    isLoadingEarningReport,
    t,
    weeklyReportLink,
  ])

  return (
    <Container
      sx={{
        marginBottom: {
          md: 0,
          xs: 4,
        },
      }}
    >
      <Grid container rowGap={4} sx={{ p: 2 }}>
        {showDownloadWeeklyReport && (
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end">
              {renderDownloadWeeklyReportButton()}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography sx={{ color: grey[800], mb: 2 }} variant="h5">
                {t('earnings.stats.title')}
              </Typography>
              <Grid alignItems="stretch" columns={15} container spacing={2}>
                <Grid item lg={3} xs={15}>
                  <EarningsStatsCard
                    icon={
                      <CalendarMonthIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingEarningStats}
                    title={t('earnings.stats.start_date')}
                    titleVariant="h5"
                    value={earningStats?.start_date ?? '-'}
                  />
                </Grid>
                <Grid item lg={3} xs={15}>
                  <EarningsStatsCard
                    icon={
                      <CalendarMonthIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingEarningStats}
                    title={t('earnings.stats.end_date')}
                    titleVariant="h5"
                    value={earningStats?.end_date ?? '-'}
                  />
                </Grid>
                <Grid item lg={3} xs={15}>
                  <EarningsStatsCard
                    icon={
                      <AttachMoneyIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingEarningStats}
                    title={t('earnings.stats.tax_amount')}
                    titleVariant="h5"
                    value={formatCurrency(earningStats?.tax_amount ?? 0)}
                  />
                </Grid>
                <Grid item lg={3} xs={15}>
                  <EarningsStatsCard
                    icon={
                      <AttachMoneyIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingEarningStats}
                    title={t('earnings.stats.tips')}
                    titleVariant="h5"
                    value={formatCurrency(earningStats?.tip_amount ?? 0)}
                  />
                </Grid>
                <Grid item lg={3} xs={15}>
                  <EarningsStatsCard
                    icon={
                      <AttachMoneyIcon
                        sx={{
                          color: grey[600],
                        }}
                      />
                    }
                    loading={isLoadingEarningStats}
                    title={t('earnings.stats.net_pay')}
                    titleVariant="h5"
                    value={formatCurrency(earningStats?.net_pay ?? 0)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack alignItems="baseline" direction="row">
                <Typography sx={{ color: grey[800], mb: 2 }} variant="h5">
                  {t('earnings.latest_rides')}
                </Typography>
              </Stack>
              <RidesTable
                columns={headers}
                containerStyle={{ height: 500 }}
                currentPage={earningRidesPage}
                data={earningRides ?? []}
                isLoading={isLoadingEarningRides}
                onChangePagination={handleChangePagination}
                pageSize={earningRidesPerPage}
                totalItems={totalEarningRides}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default Rides
